import { defaultTimezone } from '@/utils/constants';
import useSession from '@/utils/hooks/useSession';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import moment from 'moment-timezone';
import { useCallback } from 'react';

export function hubLocaleToBcp47(locale) {
  if (!locale || locale === 'en') {
    return 'en-US';
  }
  if (locale === 'fr-ca') {
    return 'fr-CA';
  }
  return locale;
}

const INTL_FORMAT_OPTIONS = {
  date: { day: '2-digit', month: 'short', year: 'numeric' },
  time: { hour: '2-digit', minute: '2-digit', hour12: false },
  dateTime: {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  dateTime12Hour: {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  },
  dateTimeWithSeconds: {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  },
  monthDayYearShort: {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  },
  monthDayYearLong: {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  },
  isoDate: { year: 'numeric', month: '2-digit', day: '2-digit' },
};

export function getIntlOptionsForFormat(formatKey) {
  return INTL_FORMAT_OPTIONS[formatKey] ?? null;
}

function shouldUseIntlLocale(locale) {
  return Boolean(locale && locale !== 'en');
}

function formatMomentWithIntl(
  time,
  formatKey,
  locale,
  timezone,
  displayTimezone
) {
  const options = getIntlOptionsForFormat(formatKey);
  if (!options) {
    return null;
  }

  const intlOptions = {
    ...options,
    timeZone: timezone,
  };

  if (displayTimezone) {
    intlOptions.timeZoneName = 'short';
  }

  const formatted = new Intl.DateTimeFormat(
    hubLocaleToBcp47(locale),
    intlOptions
  ).format(time.toDate());

  if (formatKey === 'dateTimeWithMilliSeconds') {
    return `${formatted}.${time.format('SSS')}`;
  }

  return formatted;
}

export function getUTCEpochTime() {
  return moment().valueOf();
}

export function getUTCEpochTimeSeconds() {
  return Math.floor(moment().valueOf() / 1000);
}

// Get the current time as either a string or moment.js object.
export function getCurrentTime(timezone = 'GMT', raw = false) {
  const time = moment().tz(timezone);
  return raw ? time : time.format();
}

export function getTimezones(locale = 'en-US') {
  // Moment has well formatted data for timezone name and utc offset, but not the long
  // name. We use the Intl API to get the long name of the timezone.
  const intlTimezoneNames = Intl.supportedValuesOf('timeZone');
  const momentTimezoneNames = moment.tz.names();
  const supportedTimezoneNames = momentTimezoneNames.filter((name) =>
    intlTimezoneNames.includes(name)
  );

  const timeZoneStringMap = {};
  for (const timezoneName of supportedTimezoneNames) {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: timezoneName,
      timeZoneName: 'long',
    });
    const result = formatter.formatToParts(new Date());
    const longName = result.find((part) => part.type === 'timeZoneName').value;

    const utcOffset = moment.tz(timezoneName).utcOffset() / 60;
    let utcOffsetString = utcOffset.toString(); // 1, 10, -1, -10
    if (!utcOffsetString.startsWith('-')) {
      utcOffsetString = `+${utcOffsetString}`;
    } // +1, +10, -1, -10
    if (utcOffsetString.length === 2) {
      utcOffsetString = utcOffsetString[0] + '0' + utcOffsetString[1];
    } // +01, +10, -01, -10
    utcOffsetString = `UTC${utcOffsetString}:00`; // UTC+01:00, UTC+10:00, UTC-01:00, UTC-10:00

    const formattedString = `${timezoneName} (${utcOffsetString}) - ${longName}`;

    timeZoneStringMap[timezoneName] = formattedString;
  }

  return timeZoneStringMap;
}

export function getStandardFormat(format, timezone = false) {
  const formats = {
    iso: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    date: 'DD MMM YYYY',
    isoDate: 'YYYY-MM-DD',
    isoDateTimeWithSeconds: 'YYYY-MM-DD HH:mm:ss',
    time: 'HH:mm',
    dateTime: 'DD MMM YYYY HH:mm',
    dateTime12Hour: 'DD MMM YYYY hh:mm',
    dateTimeWithSeconds: 'DD MMM YYYY HH:mm:ss',
    dateTimeWithMilliSeconds: 'DD MMM YYYY HH:mm:ss.SSS',
    monthDayYearShort: 'MMM DD, YYYY',
  };
  const selectedFormat = formats[format];

  if (!selectedFormat)
    throw new Error(`Invalid format passed to datetime module: ${format}`);

  if (timezone) {
    return `${selectedFormat} z`;
  }
  return selectedFormat;
}

// Hook to allow components to access the renderTime function.
export function useRenderTime() {
  const { user } = useSession();
  const { locale } = useHubTranslation();
  const renderTimeBound = useCallback(
    (timeNoOffset, format, args = {}) =>
      renderTime(timeNoOffset, format, user, { ...args, locale }),
    [locale, user]
  );
  return {
    renderTime: renderTimeBound,
  };
}

// Render time in the given format.
export function renderTime(timeNoOffset, format, user, args = {}) {
  const { nullValue, timezoneOverride, displayTimezone, locale } = args;

  if (
    timeNoOffset === null ||
    timeNoOffset === undefined ||
    timeNoOffset === ''
  ) {
    return nullValue || 'NULL';
  }

  let formatToUse;
  let standardFormatKey = format;
  try {
    formatToUse = getStandardFormat(format, displayTimezone);
  } catch {
    formatToUse = format;
    standardFormatKey = null;
  }

  const timezoneRaw = timezoneOverride || user?.timezone;
  const timezone = toIANATimezone(timezoneRaw);

  let time;
  if (
    typeof timeNoOffset === 'string' &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timeNoOffset)
  ) {
    time = moment.tz(timeNoOffset, 'YYYY-MM-DD HH:mm:ss', timezone);
  } else {
    time = moment(timeNoOffset, true).tz(timezone);
  }

  if (!time.isValid()) return 'Invalid Date Format';

  if (shouldUseIntlLocale(locale) && standardFormatKey) {
    const intlFormatted = formatMomentWithIntl(
      time,
      standardFormatKey,
      locale,
      timezone,
      displayTimezone
    );
    if (intlFormatted) {
      return intlFormatted;
    }
  }

  return time.format(formatToUse);
}

// Given 2 date strings or Date objects, return the difference in milliseconds.
export function compareTimes(a, b) {
  return moment(b) - moment(a);
}

export function convertBadSqlTimestamp(timestamp, actualTimezone) {
  // When the SQL utility gets a timestamp with no offset, I.E '2023-05-05 14:09:02.187',
  // it will assume the timestamp is GMT and re-write it to '2023-05-05 14:09:02.187Z'.
  // This function allows you to unwind that into whatever timezone the timestamp was
  // supposed to be in.

  // Default assumptions on timezone.
  if (!actualTimezone) {
    actualTimezone = defaultTimezone;
  }

  const time = moment(timestamp);

  const minutesOffset = moment.tz(actualTimezone).utcOffset();
  time.add(-minutesOffset, 'minutes');

  // Grabbing via zone will always return the offset for current date, to calculate
  // daylight savings time, we check the current DST bool and the DST bool for the
  // timestamp we are acting on.
  const isDst = moment().isDST();
  const isDstTimestamp = moment(timestamp).isDST();
  const dstOffset = isDst - isDstTimestamp;
  time.add(dstOffset, 'hours');

  return time.format();
}

/**
 * Turn a stored ISO date (YYYY-MM-DD) into the moment instance MUI's
 * AdapterMoment expects for a picker `value`. Exists so feature code can honour
 * the "no direct moment imports" rule — the adapter needs an object, and every
 * other export here returns a string.
 */
export function toPickerValue(isoDate) {
  if (!isoDate) return null;

  const time = moment(isoDate, 'YYYY-MM-DD', true);
  return time.isValid() ? time : null;
}

/**
 * Reduce a value to a plain YYYY-MM-DD day, or '' when it is not one.
 *
 * Callers compare stored date-only columns against form input, which arrive in different
 * shapes: a picker sends YYYY-MM-DD, while a value read back off a row is a Date or a full
 * ISO timestamp. Parsed strictly in those shapes only — a lenient parse would hand back a day
 * for '2026-05-26garbage' or '2026-02-30' while the caller went on to write the original value
 * to a DATE column.
 */
export function toIsoDay(value) {
  if (!value) return '';

  if (value instanceof Date) {
    const fromDate = moment.utc(value);
    return fromDate.isValid() ? fromDate.format('YYYY-MM-DD') : '';
  }

  const text = String(value).trim();
  if (!text) return '';

  const day = moment.utc(text, ['YYYY-MM-DD', moment.ISO_8601], true);
  return day.isValid() ? day.format('YYYY-MM-DD') : '';
}

/** Inverse of toPickerValue: a picker's moment back to a storable ISO date. */
export function fromPickerValue(pickerValue) {
  if (!pickerValue || typeof pickerValue.isValid !== 'function') return null;

  return pickerValue.isValid() ? pickerValue.format('YYYY-MM-DD') : null;
}

/**
 * Render a stored ISO date for display. Date-only values carry no time, so this
 * deliberately skips the timezone handling in renderTime, which would shift them.
 */
export function formatIsoDate(isoDate, formatKey = 'monthDayYearShort') {
  const time = toPickerValue(isoDate);
  return time ? time.format(getStandardFormat(formatKey)) : null;
}

// Convert display date format back to ISO date format for filtering
export function parseDisplayDateToIsoDate(displayDate) {
  if (!displayDate) return null;

  // Handle common display formats: "Oct 21, 2025", "21 Oct 2025", etc.
  const time = moment(
    displayDate,
    ['MMM D, YYYY', 'DD MMM YYYY', 'MMM DD, YYYY'],
    true
  );

  if (!time.isValid()) return null;
  return time.format('YYYY-MM-DD');
}

// Convert any date input to ISO date format for consistent filtering
export function normalizeDateForFiltering(dateInput) {
  if (!dateInput) return null;

  // If it's already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }

  // Try to parse as display format first
  const parsed = parseDisplayDateToIsoDate(dateInput);
  if (parsed) return parsed;

  // Fallback to moment parsing
  const time = moment(dateInput);
  return time.isValid() ? time.format('YYYY-MM-DD') : null;
}

const TIMEZONE_ALIASES = {
  EST: 'America/New_York',
  EDT: 'America/New_York',
  Eastern: 'America/New_York',
  'Eastern Time': 'America/New_York',
  'Eastern Standard Time': 'America/New_York',
  'Eastern Daylight Time': 'America/New_York',

  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  Central: 'America/Chicago',
  'Central Time': 'America/Chicago',
  'Central Standard Time': 'America/Chicago',
  'Central Daylight Time': 'America/Chicago',

  MST: 'America/Denver',
  MDT: 'America/Denver',
  Mountain: 'America/Denver',
  'Mountain Time': 'America/Denver',
  'Mountain Standard Time': 'America/Denver',
  'Mountain Daylight Time': 'America/Denver',

  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  Pacific: 'America/Los_Angeles',
  'Pacific Time': 'America/Los_Angeles',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Pacific Daylight Time': 'America/Los_Angeles',

  AKST: 'America/Anchorage',
  AKDT: 'America/Anchorage',
  Alaska: 'America/Anchorage',
  'Alaska Time': 'America/Anchorage',

  HST: 'Pacific/Honolulu',
  Hawaii: 'Pacific/Honolulu',
  'Hawaii Time': 'Pacific/Honolulu',

  // Other common abbreviations
  UTC: 'Etc/UTC',
  GMT: 'Etc/GMT',
  BST: 'Europe/London', // British Summer Time
  IST: 'Asia/Kolkata', // India Standard Time
  WET: 'Europe/Lisbon', // Western European Time
  WEST: 'Europe/Lisbon', // Western European Summer Time
  CET: 'Europe/Paris', // Central European Time
  CEST: 'Europe/Paris', // Central European Summer Time
  EET: 'Europe/Athens', // Eastern European Time
  EEST: 'Europe/Athens', // Eastern European Summer Time
  JST: 'Asia/Tokyo',
  KST: 'Asia/Seoul',
  AEST: 'Australia/Sydney',
  AEDT: 'Australia/Sydney',
  ACST: 'Australia/Adelaide',
  ACDT: 'Australia/Adelaide',
  AWST: 'Australia/Perth',
  NZST: 'Pacific/Auckland',
  NZDT: 'Pacific/Auckland',
  SGT: 'Asia/Singapore',
  HKT: 'Asia/Hong_Kong',
  CST_CHINA: 'Asia/Shanghai', // Sometimes CST is China Standard Time
  MSK: 'Europe/Moscow',
  ART: 'America/Argentina/Buenos_Aires',
  AST: 'America/Halifax', // Atlantic Standard Time
  // Add more as needed
};

// Lazily built on first call. Rebuilding this Set per call was O(N*M) because
// `Intl.supportedValuesOf('timeZone')` was invoked inside a `.filter` over
// `moment.tz.names()` (~600 entries) and re-materialized its ~400-entry array
// every iteration. In hot paths like DataGrid `valueFormatter`s this burned
// ~120ms per cell render (see renderTime -> toIANATimezone).
// Lazy (vs module-level) initialization keeps tests that auto-mock this module
// safe even when their moment-timezone mock omits tz.names.
// Fixing the source is more durable than chasing every callsite.
let _ianaTimezoneSet = null;
function getIANATimezoneSet() {
  if (!_ianaTimezoneSet) {
    const intlSupported = new Set(Intl.supportedValuesOf('timeZone'));
    _ianaTimezoneSet = new Set(
      moment.tz.names().filter((name) => intlSupported.has(name))
    );
  }
  return _ianaTimezoneSet;
}

export function toIANATimezone(tz) {
  if (!tz) return 'America/New_York';
  const cleaned = tz.trim();
  if (getIANATimezoneSet().has(cleaned)) return cleaned;
  const alias =
    TIMEZONE_ALIASES[cleaned] || TIMEZONE_ALIASES[cleaned.toUpperCase()];
  if (alias) return alias;
  return 'America/New_York';
}
